import React from 'react';
import ImmutablePropTypes from 'react-immutable-proptypes';
import track from 'react-tracking';
import {withRouter} from 'react-router';
import {List} from 'immutable';
import {getModelForResourceType} from 'resources/modelRegistry';
import {callAction} from 'client/framework';
import {history} from 'client/history';
import createAssignmentActions from '../CreateAssignment.actions';
import createAssignmentStore from '../CreateAssignment.store';
import LoadingIndicator from 'client/generic/LoadingIndicator.react';
import CreateAssignmentRecipientGroup from './CreateAssignmentRecipientGroup/CreateAssignmentRecipientGroup.react';
import CreateAssignmentAllRecipientsOption from './CreateAssignmentAllRecipientsOption/CreateAssignmentAllRecipientsOption.react';
import wizardActions from 'client/generic/Wizard/Wizard.actions';
import SimpleModal from 'generic/SimpleModal/SimpleModal.react';
import Button from 'sg/Button/Button.react';
import './create-assignment-recipients.scss';
import {getBaseUserQuery} from 'client/User/UserQueryUtil';
import sessionStore from 'client/Session/SessionStore';
import {addToast, Banner} from '@albert-io/atomic';

const isBrowserEnv = process.env.IS_BROWSER;

@track({component: 'Step 1: Choose Recipients', feature: 'ACF'})
class CreateAssignmentRecipients extends React.Component {
  static propTypes = {
    licensedSubjects: ImmutablePropTypes.listOf(getModelForResourceType('subject_v2')),
    tracking: ImmutablePropTypes.object,
    params: {
      classroomId: ImmutablePropTypes.string
    }
  };

  constructor(props) {
    super(props);
    this.state = {
      classrooms: new List()
    };
  }

  componentDidMount() {
    global.scrollTo(0, 0);
    this.getClassrooms();
  }

  componentDidUpdate() {
    const {classrooms} = this.state;
    if (!createAssignmentStore.getIsEditMode()) {
      const storeClassrooms = createAssignmentStore.getMyClassrooms();
      if (classrooms.isEmpty() && !storeClassrooms.isEmpty()) {
        this.setState({classrooms: storeClassrooms});
      }
    }
  }

  getClassrooms = () => {
    const {
      params: {classroomId}
    } = this.props;
    const isEditMode = createAssignmentStore.getIsEditMode();

    const classrooms = isEditMode
      ? // only one classroom based on the current classroom_id in the url should be returned
        createAssignmentStore
          .getAssignmentModel()
          .getClassrooms()
          .filter((classroom) => classroom.get('id') === classroomId)
      : createAssignmentStore.getMyClassrooms(); // This is badly named. It returns ALL active classrooms by teacher.

    this.setState({classrooms});
  };

  handleCancelButtonClick = () => {
    this.props.tracking.trackEvent({type: 'button', content: 'Go Back', event: 'click'});
    callAction(createAssignmentActions.RESET_STORE);
    if (isBrowserEnv) {
      /*
        Redirect direct URL entrants to classes.
        Not 100% fool-proof but will catch many cases.
      */
      if (global.history.length > 2) {
        history.goBack();
      } else {
        history.pushState(null, `/classes`);
      }
    }
  };

  generateRecipientGroups() {
    const {classrooms} = this.state;
    const userQuery = getBaseUserQuery(sessionStore.getUserId());

    if (!userQuery.isResourcePopulated()) {
      return null;
    }

    const userSchool = userQuery.getResource().getSchool();
    if (!userSchool || userSchool.isEmpty() || classrooms.isEmpty()) {
      return <span>You must create a class before you can publish an assignment.</span>;
    }

    const selectAll = (
      <li
        className='acf__recipients-list-item acf__recipient-group-wrapper'
        role='tab'
        aria-label='Select All Recipients'
      >
        <CreateAssignmentAllRecipientsOption
          recipients={createAssignmentStore.getSelectedRecipients()}
          classrooms={classrooms}
        />
      </li>
    );

    const groupList = classrooms.map((classroom, key) => {
      return (
        <li
          className='acf__recipients-list-item acf__recipient-group-wrapper'
          key={key}
          role='tab'
          aria-label='Select Recipient'
        >
          <CreateAssignmentRecipientGroup classroom={classroom} />
        </li>
      );
    });
    return (
      <ul className='acf__recipients-list'>
        {/* double check this is edit mode */}
        {!createAssignmentStore.getIsEditMode() && selectAll}
        {groupList}
      </ul>
    );
  }

  render() {
    const {classrooms} = this.state;
    const hasLicense = !this.props.licensedSubjects.isEmpty();
    const hasSelectedRecipients = !createAssignmentStore.getSelectedRecipients().isEmpty();
    if (!createAssignmentStore.hasReceivedMyClassrooms()) {
      return <LoadingIndicator />;
    }
    if (classrooms.isEmpty()) {
      return (
        <Banner className='u-display_flex'>
          <Banner.Icon className='m-banner__icon' icon='exclamation-triangle' iconStyle='regular' />
          You must create a class before you may publish any assignments.
        </Banner>
      );
    }
    const outOfScopeSubjectModal = createAssignmentStore.shouldShowOutOfScopeSubjectModal() ? (
      <OutOfScopeSubjectModal
        classroomsWithOutOfScopeSubjects={createAssignmentStore.getClassroomsWithOutOfScopeSubjects()}
        licensedSubjects={this.props.licensedSubjects}
      />
    ) : null;
    return (
      <div>
        <div className='acf__recipients-wrapper' role='tablist'>
          {this.generateRecipientGroups()}
        </div>
        <div className='acf__btn-wrapper'>
          <button
            className='acf__btn acf__btn--default acf__btn--cancel'
            onClick={this.handleCancelButtonClick}
            type='button'
          >
            Go Back
          </button>
          <button
            className='acf__btn acf__btn--primary acf__btn--next'
            disabled={
              !hasSelectedRecipients ||
              createAssignmentStore.isAddSubjectsToOutOfScopeClassroomsPending()
            }
            onClick={() => {
              this.props.tracking.trackEvent({type: 'button', content: 'Next', event: 'click'});
              if (createAssignmentStore.doSelectedClassroomsLackSubjectScope() && !hasLicense) {
                callAction(createAssignmentActions.SET_SHOW_OUT_OF_SCOPE_SUBJECT_MODAL, true);
                return;
              }
              callAction(
                createAssignmentActions.ADD_OUT_OF_SCOPE_SUBJECTS_TO_CLASSROOMS,
                hasLicense
              );
              createAssignmentStore
                .getAddOutOfScopeSubjectsToClassroomsPromise()
                .then(() => {
                  callAction(wizardActions.ADVANCE_STEP);
                })
                .catch((e) => {
                  logger.error('Error adding subjects to classroom %j', e);
                  addToast({
                    message:
                      'We encountered an error when trying to add subjects to your classroom.',
                    title: 'Something went wrong',
                    type: 'danger'
                  });
                });
            }}
            type='button'
          >
            Next
          </button>
        </div>
        {outOfScopeSubjectModal}
      </div>
    );
  }
}

class OutOfScopeSubjectModal extends React.PureComponent {
  static propTypes = {
    classroomsWithOutOfScopeSubjects: ImmutablePropTypes.map,
    licensedSubjects: ImmutablePropTypes.listOf(getModelForResourceType('subject_v2'))
  };

  closeModal() {
    if (createAssignmentStore.isAddSubjectsToOutOfScopeClassroomsPending()) {
      return;
    }
    callAction(createAssignmentActions.SET_SHOW_OUT_OF_SCOPE_SUBJECT_MODAL, false);
  }

  addSubjectsToClassrooms = () => {
    const hasLicense = !this.props.licensedSubjects.isEmpty();
    if (createAssignmentStore.isAddSubjectsToOutOfScopeClassroomsPending()) {
      return;
    }
    callAction(createAssignmentActions.ADD_OUT_OF_SCOPE_SUBJECTS_TO_CLASSROOMS, hasLicense);
    createAssignmentStore
      .getAddOutOfScopeSubjectsToClassroomsPromise()
      .then(() => {
        callAction(wizardActions.ADVANCE_STEP);
      })
      .catch((e) => {
        logger.error('Error adding subjects to classroom %j', e);
        addToast({
          message: 'We encountered an error when trying to add subjects to your classroom.',
          title: 'Something went wrong',
          type: 'danger'
        });
      });
  };

  render() {
    return (
      <SimpleModal
        title='Add subjects to classes'
        className='out-of-scope-subject-modal'
        onModalClose={this.closeModal}
      >
        <p>
          This assignment includes questions from subjects which are not in the scope of the
          following selected classrooms:
        </p>
        <ul className='out-of-scope-subject-modal__list'>
          {this.props.classroomsWithOutOfScopeSubjects
            .map((subjects, classroom) => {
              return (
                <li className='out-of-scope-subject-modal__item' key={classroom.getId()}>
                  <p>
                    <strong>{classroom.getName()}:</strong>{' '}
                    {subjects.map((subject) => subject.getName()).join(', ')}
                  </p>
                </li>
              );
            })
            .toList()}
        </ul>
        <p>Would you like to add these subjects to your classrooms?</p>
        <div className='out-of-scope-subject-modal__actions'>
          <Button
            color='gray'
            text='No'
            onClick={this.closeModal}
            disabled={createAssignmentStore.isAddSubjectsToOutOfScopeClassroomsPending()}
          />
          <Button
            color='green'
            text='Yes'
            onClick={this.addSubjectsToClassrooms}
            disabled={createAssignmentStore.isAddSubjectsToOutOfScopeClassroomsPending()}
          />
        </div>
      </SimpleModal>
    );
  }
}

export default withRouter(CreateAssignmentRecipients);
